import { Button } from '@components/common/Forms';
import { LogoBestjobsAI } from '@components/layout/LogoBj/LogoBestjobsAI';
import { useTranslation } from 'next-i18next';
import { Dispatch, SetStateAction } from 'react';

/** Props */
interface JobDescriptionOverLayerProps {
  overLayerSetter: Dispatch<SetStateAction<boolean>>;
  generateAiDescription: () => void;
  isValid?: boolean;
}

/**
 * @description Job Description Over Layer component. Asks the user if they want to manually add a description
 * or generate one with AI.
 * - `overLayerSetter` - over layer setter
 * - `generateAiDescription` - generate AI description
 * - `isValid` - if the element is valid
 */
export const JobDescriptionOverLayer = (props: JobDescriptionOverLayerProps) => {
  // Destructure props
  const {
    overLayerSetter,
    generateAiDescription,
    isValid,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Render
  // **************************
  return (
    <div
      className={`absolute inset-px z-1 flex items-center justify-center rounded bg-surface/50 backdrop-blur-xs${isValid === false ? ' !bottom-7' : ''}`}
    >
      <div className="p-6 text-center">
        {/* Logo */}
        <div className="flex w-full items-center justify-center">
          <LogoBestjobsAI />
        </div>

        {/* Title */}
        <div className="mt-2 font-semibold">
          {t('openai.add.experience.title.bia')}
        </div>

        {/* Subtitle */}
        <div className="mt-1 text-ink-medium">
          {t('openai.add.job.content.coach.subtitle')}
        </div>

        {/* Buttons */}
        <div className="flex flex-col-reverse justify-center gap-4 pt-8 md:flex-row">
          {/* Manually add description */}
          <Button
            size="sm"
            type="button"
            color="light"
            styling="outline"
            rounding="full"
            className="w-full !border-ink/15 hover:!border-transparent sm:w-auto md:!px-6"
            onClick={() => {
              overLayerSetter(false);
            }}
          >
            {t('openai.add.experience.content.manually')}
          </Button>

          {/* Generate AI description */}
          <Button
            size="sm"
            type="button"
            color="primary-light"
            styling="outline"
            rounding="full"
            className="w-full sm:w-auto md:!px-6"
            onClick={() => {
              overLayerSetter(false);
              generateAiDescription();
            }}
          >
            {t('openai.add.experience.content.bia')}
          </Button>
        </div>
      </div>
    </div>
  );
};
